import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AIAyarlarFormu } from '../components/AIAyarlarFormu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user, logout, checkAuth } = useAuth();
  const { theme, themeName, changeTheme, THEMES } = useTheme();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '', phone: '', unvan: '', email: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || '',
        unvan: user.unvan || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg({ type: '', text: '' });
    try {
      await axios.put(`${API}/auth/profile`, profileData, { withCredentials: true });
      setProfileMsg({ type: 'success', text: 'Profil bilgileri güncellendi' });
      if (checkAuth) checkAuth();
    } catch (error) {
      setProfileMsg({ type: 'error', text: error.response?.data?.detail || 'Güncelleme sırasında hata oluştu' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Lütfen tüm alanları doldurun' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Yeni şifreler eşleşmiyor' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Yeni şifre en az 6 karakter olmalı' });
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        { withCredentials: true }
      );

      setMessage({ type: 'success', text: 'Şifreniz başarıyla değiştirildi' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Change password error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Şifre değiştirilirken hata oluştu' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bg}`}>
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Dashboard
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Görevler
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/calendar')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Takvim
              </button>
              {(user?.is_admin || user?.is_admin_assistant) && (
                <>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                  >
                    Kullanıcılar
                  </button>
                </>
              )}
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 border-b-2 border-blue-500">
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">
                      {user?.is_admin ? 'Admin' : user?.is_admin_assistant ? 'Admin Yardımcısı' : 'Kullanıcı'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  Çıkış
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Profil Ayarları</h2>
            <p className="text-gray-400">Hesap bilgilerinizi yönetin</p>
          </div>

          {/* Profil Bilgileri Düzenleme */}
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Kullanıcı Bilgileri</CardTitle>
              <CardDescription className="text-gray-400">Profil bilgilerinizi güncelleyin</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div className="flex items-center space-x-4 mb-4">
                  <img src={user?.picture} alt={user?.name} className="w-14 h-14 rounded-full" />
                  <div>
                    <p className="text-sm text-gray-400">@{user?.username}</p>
                    <p className="text-xs text-gray-500">
                      {user?.is_admin ? 'Admin' : user?.is_admin_assistant ? 'Admin Yardımcısı' : 'Kullanıcı'}
                      {user?.department === 'yatirim_insaat' ? ' | Yatırım ve İnşaat' : user?.department === 'bakim_onarim' ? ' | Bakım-Onarım' : user?.department === 'emlak_kamulastirma' ? ' | Emlak ve Kamulaştırma' : ''}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Ad Soyad</Label>
                    <Input value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Telefon</Label>
                    <Input type="tel" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white" placeholder="05XX XXX XX XX" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Ünvan</Label>
                    <Select value={profileData.unvan || 'none'} onValueChange={(v) => setProfileData({...profileData, unvan: v === 'none' ? '' : v})}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="Ünvan seçin" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="none">Seçilmedi</SelectItem>
                        <SelectItem value="insaat_muhendisi">İnşaat Mühendisi</SelectItem>
                        <SelectItem value="makine_muhendisi">Makine Mühendisi</SelectItem>
                        <SelectItem value="elektrik_muhendisi">Elektrik/Elektronik Mühendisi</SelectItem>
                        <SelectItem value="orman_muhendisi">Orman Mühendisi</SelectItem>
                        <SelectItem value="mimar">Mimar</SelectItem>
                        <SelectItem value="buro_memuru">Büro Memuru</SelectItem>
                        <SelectItem value="grafiker">Grafiker</SelectItem>
                        <SelectItem value="tekniker">Tekniker</SelectItem>
                        <SelectItem value="teknisyen">Teknisyen</SelectItem>
                        <SelectItem value="mudur">Müdür</SelectItem>
                        <SelectItem value="sef">Şef</SelectItem>
                        <SelectItem value="vhki">Veri Hazırlama ve Kontrol İşletmeni</SelectItem>
                        <SelectItem value="kimya_muhendisi">Kimya Mühendisi</SelectItem>
                        <SelectItem value="ziraat_muhendisi">Ziraat Mühendisi</SelectItem>
                        <SelectItem value="spor_uzmani">Spor Uzmanı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Email</Label>
                    <Input type="email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white" />
                  </div>
                </div>

                {profileMsg.text && (
                  <div className={`p-3 rounded-lg text-sm ${profileMsg.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-red-500/20 text-red-400 border border-red-500/50'}`}>
                    {profileMsg.text}
                  </div>
                )}

                <Button type="submit" disabled={profileSaving} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {profileSaving ? 'Kaydediliyor...' : 'Bilgileri Güncelle'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Tema Seçimi */}
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Tema Rengi</CardTitle>
              <CardDescription className="text-gray-400">Arayüz rengini kendinize göre ayarlayın</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {Object.entries(THEMES).map(([key, t]) => (
                  <button
                    key={key}
                    onClick={() => changeTheme(key)}
                    data-testid={`theme-option-${key}`}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      themeName === key ? 'border-white scale-105' : 'border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full" style={{ backgroundColor: t.preview }}></div>
                    <span className="text-xs text-gray-300">{t.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Yapay Zekâ — kullanıcının KENDİ anahtarları (tek giriş yeri) */}
          <Card className="bg-gray-900 border-gray-800 mb-6" data-testid="profil-yapay-zeka-karti">
            <CardHeader>
              <CardTitle className="text-white">Yapay Zekâ</CardTitle>
              <CardDescription className="text-gray-400">
                API anahtarlarının tek giriş yeri burasıdır. Claude / ChatGPT / Gemini için ayrı anahtar ve model sürümü seçin; tüm modüller (ISY Uzman Asistan, Mevzuat Analizi, Ekipman Zekâsı) yalnızca sizin işlemlerinizde bu anahtarı kullanır.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AIAyarlarFormu />
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Şifre Değiştir</CardTitle>
              <CardDescription className="text-gray-400">
                Hesap güvenliğiniz için güçlü bir şifre kullanın
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password" className="text-white">Mevcut Şifre</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Mevcut şifrenizi girin"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-white">Yeni Şifre</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Yeni şifrenizi girin (en az 6 karakter)"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-white">Yeni Şifre (Tekrar)</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Yeni şifrenizi tekrar girin"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                {message.text && (
                  <div className={`p-3 rounded-lg ${
                    message.type === 'success' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/50'
                  }`}>
                    {message.text}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {loading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
